import { Quote, ArrowUpRight, Phone, ShieldCheck, Award, CircleCheck } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const NAVY_DEEP = "#040A1F";
const GOLD = "#F5A623";
const GOLD_DARK = "#C88B1F";
const CREAM = "#FAF7F1";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const REASONS = [{
  num: "01",
  title: "Family-run since 2009",
  body: "Local Bass Hill business. The same family answers your call, dispatches the van, and shows up at your door — every time."
}, {
  num: "02",
  title: "Fixed-price quotes in writing",
  body: "Written quote before any tool comes out. No hourly creep, no callout fee, no surprises on the invoice."
}, {
  num: "03",
  title: "Same-day across all of Sydney",
  body: "95% of repair callouts completed the same day, most within 3 hours. From Eastern Suburbs to Penrith."
}, {
  num: "04",
  title: "12-month workmanship warranty",
  body: "Every repair backed in writing — not just email. If our work fails inside the window, we come back free."
}, {
  num: "05",
  title: "Fully licensed & insured",
  body: "NSW Fair Trading licensed, $20M public liability, fully insured technicians. Safe to have on your property."
}, {
  num: "06",
  title: "Every brand, every door type",
  body: "B&D, Merlin, Chamberlain, ATA, Steel-Line, Gliderol. Sectional, roller, tilt, panel-lift. One number for any door."
}];
const BADGES = ["NSW Fair Trading", "$20M Public Liability", "Fully Insured Techs", "Trustpilot · Oneflare · hipages"];
export default function WhyChooseRepair() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY
  }} aria-labelledby="why-choose-heading">
      {/* Dark texture base */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
      background: `radial-gradient(ellipse at 15% 30%, ${NAVY_LIGHT}66 0%, transparent 50%), radial-gradient(ellipse at 85% 80%, ${NAVY_DEEP} 0%, transparent 60%)`
    }} />

      {/* Subtle grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        {/* === Top header strip === */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-16 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD}66`,
            backgroundColor: `${GOLD}14`
          }}>
              <Award className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD
          }}>
              Why Gold Garage Door
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${GOLD}40`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: "rgba(255,255,255,0.4)"
        }}>
            6 Reasons · Family-Run · Sydney
          </div>
        </div>

        {/* === SPLIT LAYOUT === */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 xl:gap-20">

          {/* === LEFT — Manifesto === */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            {/* Editorial callout bar */}
            <div className="flex items-stretch gap-5 mb-7">
              <div aria-hidden className="w-[3px] flex-shrink-0" style={{
              background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD}26 100%)`
            }} />
              <div>
                <Quote className="h-8 w-8 mb-3" strokeWidth={1.5} style={{
                color: GOLD
              } as React.CSSProperties} />
                <p className="font-bold text-white" style={{
                fontFamily: FONT_HEAD,
                fontSize: "clamp(1.625rem, 3.2vw, 2.5rem)",
                lineHeight: 1.15,
                letterSpacing: "-0.025em"
              }}>
                  When you call, you get the
                  <br className="hidden md:block" />{" "}
                  <span style={{
                  color: GOLD
                }}>same family</span> that&rsquo;s
                  been fixing Sydney&rsquo;s garage doors since 2009.
                </p>
              </div>
            </div>

            {/* Body */}
            <p className="text-[16px] leading-[1.7] max-w-md mb-7" style={{
            color: "rgba(255,255,255,0.72)"
          }}>
              No call centres. No subcontractors. No hourly creep. Just local
              Sydney technicians who answer the phone, show up on time, and
              stand behind every repair in writing.
            </p>

            {/* Signature */}
            <div className="flex items-center gap-3 pt-5 border-t" style={{
            borderColor: "rgba(255,255,255,0.10)"
          }}>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_HEAD
            }}>
                GG
              </div>
              <div>
                <div className="text-[13px] font-bold text-white" style={{
                fontFamily: FONT_HEAD,
                letterSpacing: "-0.005em"
              }}>
                  The Gold Family
                </div>
                <div className="text-[11px] font-semibold tracking-[0.16em] uppercase mt-0.5" style={{
                color: "rgba(255,255,255,0.45)"
              }}>
                  Bass Hill · Sydney NSW
                </div>
              </div>
            </div>

            {/* Mini CTA */}
            <a href="tel:0485000933" className="group mt-8 inline-flex items-center gap-3 text-[14px] font-bold transition-colors" style={{
            color: "white",
            fontFamily: FONT_BODY
          }} onMouseEnter={e => e.currentTarget.style.color = GOLD} onMouseLeave={e => e.currentTarget.style.color = "white"}>
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors" style={{
              borderColor: `${GOLD}66`,
              backgroundColor: `${GOLD}1F`,
              color: GOLD
            }}>
                <Phone className="h-4 w-4" strokeWidth={2.5} />
              </span>
              Talk to the family &mdash; 0485 000 933
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </a>
          </div>

          {/* === RIGHT — 6 Reasons editorial list === */}
          <div className="lg:col-span-7">
            <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-6" style={{
            color: "rgba(255,255,255,0.4)"
          }}>
              — Six reasons people pick us
            </div>

            <div className="border-t" style={{
            borderColor: "rgba(255,255,255,0.10)"
          }}>
              {REASONS.map(reason => <ReasonRow key={reason.num} reason={reason} />)}
            </div>
          </div>
        </div>

        {/* === Bottom badges strip === */}
        <div className="mt-16 md:mt-20">
          <div className="relative rounded-2xl border p-5 md:p-6" style={{
          borderColor: `${GOLD}26`,
          backgroundColor: "rgba(255,255,255,0.03)",
          backdropFilter: "blur(8px)"
        }}>
            <div className="flex items-center justify-center gap-3 md:gap-8 flex-wrap">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="h-5 w-5" strokeWidth={2} style={{
                color: GOLD
              } as React.CSSProperties} />
                <span className="text-[11px] font-bold tracking-[0.2em] uppercase" style={{
                color: "rgba(255,255,255,0.6)"
              }}>
                  Credentials
                </span>
              </div>

              {BADGES.map((badge, idx) => <div key={badge} className="flex items-center gap-3 md:gap-8">
                  <span aria-hidden className="hidden md:block w-1 h-1 rounded-full" style={{
                backgroundColor: `${GOLD}80`
              }} />
                  <span className="text-[12px] md:text-[13px] font-semibold text-white" style={{
                fontFamily: FONT_BODY
              }}>
                    {badge}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />
    </section>;
}
function ReasonRow({
  reason
}: {
  reason: {
    num: string;
    title: string;
    body: string;
  };
}) {
  return <div className="group relative grid grid-cols-[60px_1fr_24px] md:grid-cols-[88px_1fr_32px] items-start gap-4 md:gap-6 py-6 md:py-7 border-b transition-all duration-300 cursor-default" style={{
    borderColor: "rgba(255,255,255,0.10)"
  }} onMouseEnter={e => {
    const accent = e.currentTarget.querySelector("[data-accent]") as HTMLElement;
    if (accent) accent.style.width = "100%";
  }} onMouseLeave={e => {
    const accent = e.currentTarget.querySelector("[data-accent]") as HTMLElement;
    if (accent) accent.style.width = "0%";
  }}>
      {/* Hover gold accent */}
      <span data-accent aria-hidden className="absolute bottom-[-1px] left-0 h-px transition-all duration-500 ease-out" style={{
      width: "0%",
      background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}00 100%)`
    }} />

      {/* Number */}
      <div className="font-extrabold leading-none transition-colors duration-300" style={{
      fontFamily: FONT_HEAD,
      color: `${GOLD}40`,
      fontSize: "clamp(1.875rem, 3vw, 2.5rem)",
      letterSpacing: "-0.025em",
      fontVariantNumeric: "tabular-nums"
    }}>
        {reason.num}
      </div>

      {/* Content */}
      <div className="min-w-0 pt-1">
        <h3 className="font-bold text-white transition-colors duration-200 mb-2 group-hover:text-[#F5A623]" style={{
        fontFamily: FONT_HEAD,
        fontSize: "clamp(1.0625rem, 1.5vw, 1.25rem)",
        lineHeight: 1.25,
        letterSpacing: "-0.012em"
      }}>
          {reason.title}
        </h3>
        <p className="text-[14px] md:text-[14.5px] leading-[1.65] max-w-xl" style={{
        color: "rgba(255,255,255,0.62)"
      }}>
          {reason.body}
        </p>
      </div>

      {/* Trailing check (visible) */}
      <div className="pt-2 flex justify-end">
        <CircleCheck className="h-5 w-5 transition-all duration-300 group-hover:scale-110" strokeWidth={1.8} style={{
        color: GOLD
      } as React.CSSProperties} />
      </div>
    </div>;
}